import React, { useState, Fragment, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { useHistory } from "react-router-dom";
import { Auth } from "aws-amplify";
import { Link } from "react-router-dom";
import LoaderButton from "../components/LoaderButton";
import { useAppContext } from "../lib/contextLib";
import { useFormFields } from "../lib/hooksLib";
import { setUserState } from "../lib/authLib";
import "./Signup.scss";

export default function Signup() {
  const [fields, handleFieldChange] = useFormFields({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    confirmationCode: "",
  });
  const history = useHistory();
  const [newUser, setNewUser] = useState(null);
  const { userHasAuthenticated } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [showLinkToLogin, setShowLinkToLogin] = useState(false);
  const temoLocalStorageAddressForNewUserDataWhileUserGettingAuthCode = 'tempNewUser';

  useEffect(() => {
    const tempNewUser = window.localStorage.getItem(temoLocalStorageAddressForNewUserDataWhileUserGettingAuthCode);

    if (tempNewUser) {
      setNewUser(JSON.parse(tempNewUser));
    }
  }, []);

  function validateForm() {
    return (
      fields.email.length > 0 &&
      fields.password.length > 0 &&
      fields.password === fields.confirmPassword
    );
  }

  function validateConfirmationForm() {
    return fields.confirmationCode.length > 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
  
    setIsLoading(true);
  
    try {
      const ourNewUser = await Auth.signUp({
        username: fields.email,
        password: fields.password,
      });

      window.localStorage.setItem(temoLocalStorageAddressForNewUserDataWhileUserGettingAuthCode, JSON.stringify(newUser));
      setIsLoading(false);
      setNewUser(ourNewUser);
      setShowLinkToLogin(false);
      setErrMsg('');
    } catch (e) {
      console.error("\n💥❌ handleSubmit() Error\n\n", e, "\n\n");
      const passwordLengthErrMsg = 'Your password must be at least 8 characters long';

      if (e.code && e.message) {
        if (/UsernameExistsException/i.test(e.code)) { // Email is already registered & confirmed
          setErrMsg(e.message);
          setShowLinkToLogin(true);
        } else if (/InvalidPasswordException/ig.test(e.code)) { // Password does not conform to Cognito password rules
          setErrMsg(`${passwordLengthErrMsg}.`);
          setShowLinkToLogin(false);
        } else if (/InvalidParameterException/ig.test(e.code)) { // Password does not conform to /^[\S]+.*[\S]+$/
          setErrMsg(`${passwordLengthErrMsg}, and cannot start or end with spaces.`);
          setShowLinkToLogin(false);
        } else { // ¯\_(ツ)_/¯ ???
          setErrMsg(e.message);
          setShowLinkToLogin(true);
        }
      }

      setIsLoading(false);
    }
  }
  
  async function handleConfirmationSubmit(event) {
    event.preventDefault();
  
    setIsLoading(true);
  
    try {
      await Auth.confirmSignUp(fields.email, fields.confirmationCode);
      await Auth.signIn(fields.email, fields.password);
      await setUserState(fields.name, fields.email); // Luke authentication thingy
  
      userHasAuthenticated(true);
      history.push("/");
      window.localStorage.removeItem(temoLocalStorageAddressForNewUserDataWhileUserGettingAuthCode);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  }

  function cancelConfirmation(e) {
    e.preventDefault();
    window.localStorage.removeItem(temoLocalStorageAddressForNewUserDataWhileUserGettingAuthCode);
    setNewUser(null);
  }

  function renderConfirmationForm() {
    return (
      <Form className="confirmation-code-wrapper" onSubmit={handleConfirmationSubmit}>
        <Form.Group controlId="confirmationCode" size="lg">
          <Form.Label>Confirmation Code</Form.Label>
          <Form.Control
            autoFocus
            type="tel"
            onChange={handleFieldChange}
            value={fields.confirmationCode}
          />
          <Form.Text muted>Please check your email for the code.</Form.Text>
        </Form.Group>
        <LoaderButton
          block
          size="lg"
          type="submit"
          variant="success"
          isLoading={isLoading}
          disabled={!validateConfirmationForm()}
        >
          Verify
        </LoaderButton>
        <div className="confirmation-code-cancellation">
          <span onClick={cancelConfirmation}>Cancel confirmation and go back to signup.</span>
        </div>
      </Form>
    );
  }

  function renderForm() {
    return (
      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="name" size="lg">
          <Form.Label>Name</Form.Label>
          <Form.Control
            autoFocus
            type="name"
            value={fields.name}
            onChange={handleFieldChange}
          />
        </Form.Group>
        <Form.Group controlId="email" size="lg">
          <Form.Label>Email</Form.Label>
          <Form.Control
            autoFocus
            type="email"
            value={fields.email}
            onChange={handleFieldChange}
          />
        </Form.Group>
        {
          errMsg.length > 1 && showLinkToLogin && (
            <Fragment>
              <p className="email-error">{errMsg}</p>
              <Link to="/login">Try logging in!</Link>
            </Fragment>
          )
        }
        <Form.Group controlId="password" size="lg" className="password-group">
          <Form.Label>Password</Form.Label>
          <p>Your password must be at least 8 characters long.</p>
          <Form.Control
            type="password"
            value={fields.password}
            onChange={handleFieldChange}
          />
        </Form.Group>
        {
          errMsg.length > 1 && !showLinkToLogin && (
            <Fragment>
              <p className="email-error">{errMsg}</p>
            </Fragment>
          )
        }
        <Form.Group controlId="confirmPassword" size="lg" className="confirm-password-group">
          <Form.Label>Confirm Password</Form.Label>
          <Form.Control
            type="password"
            onChange={handleFieldChange}
            value={fields.confirmPassword}
          />
        </Form.Group>
        <div className="passwords-match-container">
          <span>Make sure your passwords match 😊</span>
        </div>
        <LoaderButton
          className="signup-btn"
          block
          size="lg"
          type="submit"
          variant="success"
          isLoading={isLoading}
          disabled={!validateForm()}
        >
          Signup
        </LoaderButton>
      </Form>
    );
  }

  return (
    <div className="Signup">
      {newUser === null ? renderForm() : renderConfirmationForm()}
    </div>
  );
}
